// Recording and transcription consent for a live internal call. Each person can allow or withdraw
// only their own consent; starting needs everyone's agreement and an explicit Start. Status words
// come from the server's observed recording state and confirmed privacy acknowledgement only.
import type { Call } from '@bcio-voice/contracts';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Captions, Pause, Play, Radio, ShieldAlert, ShieldCheck, ShieldOff } from 'lucide-react';
import { Icon } from '../../ui/Icon.tsx';
import { Diagnostics } from '../../ui/Diagnostics.tsx';
import { useWorkspace } from '../../state/workspace.tsx';
import { AUTOMATIC_CAPTURE_TEXT, type CaptureController, type CaptureSnapshot, type CaptureView, agentControlOf, captureLabel, captureNoun, captureReminderOf, captureView, unavailableText } from '../../call/capture.ts';

const IDLE: CaptureSnapshot = { status: 'idle', capture: null, unavailable: null, problem: null, pendingConsent: null, pendingCapture: null, checking: false, stale: false };
const noopSubscribe = () => () => undefined;
const idle = () => IDLE;

/** The shared capture controller for a live call, or null when none should exist. */
export function useCallCapture(callId: string | null): { controller: CaptureController | null; snapshot: CaptureSnapshot } {
  const ws = useWorkspace();
  const [controller, setController] = useState<CaptureController | null>(null);
  useEffect(() => {
    if (!callId) return;
    const c = ws.captures.acquire(callId);
    setController(c);
    return () => {
      setController(null);
      ws.captures.release(callId);
    };
  }, [callId, ws.captures]);
  const snapshot = useSyncExternalStore(controller?.subscribe ?? noopSubscribe, controller?.getSnapshot ?? idle, controller?.getSnapshot ?? idle);
  return { controller, snapshot };
}

/** The privacy reminder at the top of every capture band (both consent models), when the API sends one. */
function CaptureReminder({ text }: { text: string | null }) {
  return text ? <p className="capture-reminder"><Icon icon={ShieldAlert} /><span>{text}</span></p> : null;
}

export function NumberCaptureBand({snapshot,actions,view}:{snapshot:CaptureSnapshot;actions:CaptureActions;view?:CaptureView}){
 const c=snapshot.capture!,n=c.number_policy!,ready=c.policy_verified&&c.capability.status==='enabled'&&c.capability.allowed&&c.can_control,busy=snapshot.pendingCapture!==null;
 // transcribing: capture is on with no recorder (transcripts only); a plain state, never a failure or a start still pending.
 const ask=n.mode==='ask',recording=c.observed_state==='recording'&&c.requested_state==='recording',transcribing=!!view?.transcriptsOnly,stopping=['pending','failed'].includes(c.privacy_ack);
 // Consent model: customer-managed ask mode starts with the agent's own attestation and no scripted confirmation line.
 const customer=c.compliance_model==='customer_managed',controls=agentControlOf(c)==='pause_stop',noun=view?.noun??captureNoun(c,undefined),startLabel=customer?captureLabel('start',noun):'Record';
 return <section className="capture-band" aria-label="Recording and transcription"><div className="capture-head"><h3 className="capture-title">Recording and transcription</h3><span className="chip">{c.observed_state==='failed'?'Recording failed':recording?(n.mode==='record'?'Recording (admin override)':ask?'Recording':'Recording (number policy)'):transcribing?(view?.status.label==='Transcribing'?'Transcribing (not recorded)':`${view?.status.label} (not recorded)`):c.requested_state==='recording'?(n.phase==='blocked'?'Off (recording blocked)':'Starting…'):c.requested_state==='paused'?(c.privacy_ack==='confirmed'?'Paused':'Pausing…'):stopping?'Stop not confirmed':'Off'}</span></div>
 <CaptureReminder text={view?.reminder??captureReminderOf(c)} />
 {n.mode==='record'&&n.admin_override?<p className="field-hint">Admin override: {n.admin_override.reason}</p>:null}
 {ask&&c.requested_state==='off'?<><div className="capture-actions"><span className="capture-action"><button type="button" className="btn btn-primary" disabled={!ready||busy||stopping||!!n.reason} onClick={actions.start}>{startLabel}</button></span></div>
  {customer?<><p className="field-hint">{n.question_text}</p><p className="field-hint">Tap {startLabel} only after everyone on the call has consented. Your tap saves this statement as your consent attestation.</p></>
  :<><p className="field-hint">Ask first: {n.question_text}</p><p className="field-hint">Tap Record only after the other party agrees. Your tap saves an immutable consent attestation.</p></>}</>:null}
 {ask&&!customer&&n.confirmation_text&&(recording||transcribing)?<p className="field-hint">Say: “{n.confirmation_text}”</p>:null}
 {controls?<div className="capture-actions">
 {c.requested_state==='recording'?<span className="capture-action"><button type="button" className="btn" disabled={busy||!c.can_control} onClick={actions.pause}>{captureLabel('pause',noun)}</button></span>:null}
 {c.requested_state==='paused'?<span className="capture-action"><button type="button" className="btn" disabled={busy||!ready||stopping} onClick={actions.resume}>{captureLabel('resume',noun)}</button></span>:null}
 {c.requested_state!=='off'?<span className="capture-action"><button type="button" className="btn" disabled={busy||!c.can_control} onClick={actions.stop}>{captureLabel('stop',noun)}</button></span>:null}
 </div>:c.requested_state!=='off'?<p className="field-hint">{AUTOMATIC_CAPTURE_TEXT}</p>:null}
 {stopping?<p role="status">{c.privacy_ack==='pending'?'Waiting for the phone system to confirm capture stopped.':'Capture stop is not confirmed. Check again.'}</p>:null}
 {n.reason?<p role="status" className="inline-reason">Recording unavailable: {n.reason.replaceAll('_',' ')}.</p>:null}
 {n.reason==='staff_notice_required'?<p className="field-hint">Accept the Recording and transcription notice at the top of the page before your next call so recording and live transcription can start.</p>:null}
 <p className="field-hint">Transcription: {view?.transcription.text??(c.observed_state==='recording'?'See the live transcript below.':'Not recording.')}</p>
 {view?<p className="field-hint">Archive: {view.archive}</p>:null}
 {snapshot.problem?<p role="alert" className="form-error">{snapshot.problem}</p>:null}
 <p className="meta">Recording and transcript access is controlled separately.</p></section>;
}

const EXPLAINER = 'Your permission covers both recording and transcription on Brandcore’s own systems, for this call only. You can withdraw it at any time. No one can agree for someone else.';

/** The dock's fixed recording slot before there is anything to show: the band's title, muted, and nothing that comes and
 * goes (no "Checking…" line). The slot keeps its height either way. */
export function CapturePlaceholder() {
  return (
    <div className="capture-note capture-placeholder" data-capture="placeholder">
      <p className="capture-title capture-placeholder-title"><Icon icon={Captions} /> Recording and transcription</p>
      <span className="capture-placeholder-bar" aria-hidden="true" />
    </div>
  );
}

/** `quiet` (the dock's call frame): before the call connects and while its capture state loads, the slot shows
 * CapturePlaceholder instead of nothing and instead of "Checking recording consent…". */
export function CaptureBand({ call, headingLevel = 3, quiet = false }: { call: Call; headingLevel?: 3 | 4; quiet?: boolean }) {
  const ws = useWorkspace();
  const live = call.state === 'active' || call.state === 'held';
  const internal = call.direction === 'internal';
  const { controller, snapshot } = useCallCapture(live ? call.id : null);

  // The call itself changed (for example its observed recording state): re-read capture state.
  const seen = useRef<{ controller: CaptureController | null; key: string } | null>(null);
  useEffect(() => {
    const key = `${call.version}|${call.recording_state}|${call.transcription_state}`;
    const prev = seen.current;
    seen.current = { controller, key };
    if (controller && prev?.controller === controller && prev.key !== key) controller.notify();
  }, [controller, call.version, call.recording_state, call.transcription_state]);
  // Transcripts only: a running transcription with the recorder off is a settled capture, not a start still waiting.
  useEffect(() => { controller?.observeTranscription(call.transcription_state); }, [controller, call.transcription_state]);

  if (!live) return quiet ? <CapturePlaceholder /> : null;
  return (
    <CaptureBandView
      snapshot={snapshot}
      view={snapshot.capture ? captureView(snapshot.capture, ws.userId, call, id => ws.userName(id), snapshot) : null}
      headingLevel={headingLevel}
      quiet={quiet}
      actions={{
        grant: () => void controller?.grant(),
        withdraw: () => void controller?.withdraw(),
        start: () => void controller?.start(),
        pause: () => void controller?.pause(),
        resume: () => void controller?.resume(),
        stop: () => void controller?.stop(),
        checkAgain: () => controller?.checkAgain(),
        disclose: () => void controller?.requestDisclosure(),
      }}
      callId={call.id}
    />
  );
}

export interface CaptureActions {
  stop?: () => void;
  disclose?: () => void;
  grant: () => void;
  withdraw: () => void;
  start: () => void;
  pause: () => void;
  resume: () => void;
  checkAgain: () => void;
}

/** Presentational band (exported for render tests). */
export function CaptureBandView({ snapshot, view, actions, callId, headingLevel = 3, quiet = false }: { snapshot: CaptureSnapshot; view: CaptureView | null; actions: CaptureActions; callId: string; headingLevel?: 3 | 4; quiet?: boolean }) {
  const Heading = headingLevel === 3 ? 'h3' : 'h4';
  const id = `capture-${callId}`;
  if (snapshot.status === 'unavailable' && snapshot.unavailable) return <p className="capture-note">{unavailableText(snapshot.unavailable)}</p>;
  if (snapshot.status === 'idle' || snapshot.status === 'loading' || (!snapshot.capture && snapshot.status !== 'error')) {
    return quiet ? <CapturePlaceholder /> : <p className="capture-note" role="status">Checking recording consent…</p>;
  }
  const c = snapshot.capture;
  if (!c || !view) return <p className="capture-note" role="alert">{snapshot.problem ?? 'Couldn’t check recording on this call.'}</p>;
  // Capture requested while I am on the call keeps the band (as when Pause showed here), even with agent control turned off.
  if ((!c.policy_verified || !c.policy_version) && !view.withdraw.visible && !(c.requested_state === 'recording' && view.self.present) && c.privacy_ack !== 'pending' && c.privacy_ack !== 'failed') {
    return (
      <div className="capture-note">
        <p>Recording and transcription aren’t set up for this workspace.</p>
        <Diagnostics diagnostic={{ code: c.capability.reason_code, detail: c.capability.detail }} />
      </div>
    );
  }

  if(c.number_policy&&c.number_policy.mode!=='off')return <NumberCaptureBand snapshot={snapshot} actions={actions} view={view} />;

  const button = (key: 'grant' | 'withdraw' | 'start' | 'pause' | 'resume', label: string, icon: typeof Radio, className: string) => {
    const a = view[key];
    if (!a.visible) return null;
    const why = a.reason ? `${id}-${key}-why` : undefined;
    return (
      <span className="capture-action" key={key}>
        <button type="button" className={`btn ${className}`} onClick={actions[key]} disabled={!a.enabled} aria-describedby={why} aria-busy={(key === 'grant' || key === 'withdraw') ? (snapshot.pendingConsent !== null || undefined) : (snapshot.pendingCapture !== null || undefined)}>
          <Icon icon={icon} /> {label}
        </button>
        {a.reason && !a.enabled ? <span id={why} className="field-hint">{a.reason}</span> : null}
      </span>
    );
  };

  return (
    <section className={`capture-band tone-${view.status.tone}`} aria-labelledby={`${id}-title`}>
      <div className="capture-head">
        <Heading id={`${id}-title`} className="capture-title"><Icon icon={Captions} /> Recording and transcription</Heading>
        <span className={`chip tone-${view.status.tone}`}>
          {view.status.tone === 'end' && view.status.label === 'Recording' ? <span className="rec-dot" aria-hidden="true" /> : null}
          {view.status.label}
        </span>
      </div>
      <CaptureReminder text={view.reminder} />
      <p className="capture-status" role="status" aria-live="polite">{view.status.detail}</p>
      {c.privacy_ack === 'pending' || c.privacy_ack === 'failed' ? <p className="field-hint">Recorder: {view.recorder}</p> : null}
      <p className={`field-hint tone-${view.transcription.tone}`}>Transcription: {view.transcription.text}</p>
      <p className="field-hint">Archive: {view.archive}</p>
      {!c.policy_verified || c.capability.status !== 'enabled' ? <p className="inline-reason">{view.agentControl === 'none' ? 'New capture is unavailable. Consent withdrawal remains available.' : 'New capture is unavailable. Pause and consent withdrawal remain available.'}</p> : null}
      <p className="field-hint">{EXPLAINER}</p>
      <ul className="capture-people" aria-label="Who has agreed">
        {view.participants.map(p => (
          <li key={p.participantId} className={p.agreed ? 'agreed' : 'waiting'}>
            <Icon icon={p.agreed ? ShieldCheck : ShieldOff} /> <span className="capture-name">{p.name}</span> <span className="meta">{p.agreed ? 'Agreed' : 'Not yet'}</span>
          </li>
        ))}
      </ul>
      {c.remote ? <div className="capture-remote"><p className="field-hint" role="status">{c.remote.detail}</p><p className="field-hint">The other party responds on their phone after the disclosure. You can agree only for yourself. Withdrawal requests stop capture; a failed or pending stop is not confirmed.</p>{c.remote.can_request ? <button type="button" className="btn" onClick={actions.disclose} disabled={snapshot.checking}>Play disclosure to other party</button> : null}</div> : null}
      <div className="capture-actions">
        {button('grant', 'Allow for this call', ShieldCheck, 'btn-primary')}
        {button('withdraw', 'Withdraw my consent', ShieldOff, '')}
        {button('start', 'Start recording', Radio, '')}
        {button('pause', captureLabel('pause', view.noun), Pause, '')}
        {button('resume', captureLabel('resume', view.noun), Play, '')}
      </div>
      {view.agentControl === 'none' && c.requested_state !== 'off' ? <p className="field-hint">{AUTOMATIC_CAPTURE_TEXT}</p> : null}
      {snapshot.checking ? <p className="field-hint" role="status">Not confirmed yet. Checking with the server…</p> : null}
      {snapshot.stale ? (
        <p className="inline-reason" role="status">
          Still waiting for the phone system to confirm. <button type="button" className="btn btn-small" onClick={actions.checkAgain}>Check again</button>
        </p>
      ) : null}
      {snapshot.problem ? <p className="form-error" role="alert">{snapshot.problem}</p> : null}
      <p className="meta">Who can listen to or read a recording later is controlled separately.</p>
    </section>
  );
}
